import type {
  CanvasSettings,
  CarouselDoc,
  ID,
  ImageLayer,
  Project,
  Slot,
  SplitterDoc,
  TextLayer,
} from '../types';
import { NEUTRAL_FILTERS } from '../filters';
import { IDENTITY_TRANSFORM } from '../transform';
import { buildPreset } from '../template';
import { SLIDE_PRESETS, TILE_ASPECTS } from '../presets';
import { uid } from '../utils';

export const BASE_EDGE = 2000;

export function makeSlot(): Slot {
  return {
    assetId: null,
    fit: 'cover',
    transform: { ...IDENTITY_TRANSFORM },
    filters: { ...NEUTRAL_FILTERS },
  };
}

export const DEFAULT_CANVAS: CanvasSettings = {
  width: BASE_EDGE,
  height: BASE_EDGE,
  aspect: '1:1',
  padding: 48,
  gap: 20,
  radius: 16,
  outerRadius: 0,
  cellFill: '#ffffff',
  background: { type: 'solid', color: '#ffffff' },
  shadow: { enabled: false, blur: 40, opacity: 18, y: 10 },
  border: { enabled: false, width: 6, color: '#ffffff' },
};

export function createProject(name = 'Untitled Collage'): Project {
  const template = buildPreset('grid-2x2')!;
  const slots: Record<string, Slot> = {};
  for (const cell of template.cells) slots[cell.id] = makeSlot();
  return {
    id: uid('proj'),
    name,
    version: 1,
    canvas: { ...DEFAULT_CANVAS, background: { ...DEFAULT_CANVAS.background }, shadow: { ...DEFAULT_CANVAS.shadow }, border: { ...DEFAULT_CANVAS.border } },
    template,
    slots,
    assets: {},
    assetOrder: [],
  };
}

/** Keep `slots` in lockstep with the template's cells. */
export function syncSlots(project: Project) {
  const ids = new Set(project.template.cells.map((c) => c.id));
  for (const cell of project.template.cells) {
    if (!project.slots[cell.id]) project.slots[cell.id] = makeSlot();
  }
  for (const id of Object.keys(project.slots)) {
    if (!ids.has(id)) delete project.slots[id];
  }
}

/* ── Grid splitter ─────────────────────────────────────────────────────── */

/** Long edge of one tile. 1080 is what every feed downscales to anyway. */
export const SPLIT_TILE_EDGE = 1080;

export function createSplitter(name = 'Untitled Grid'): SplitterDoc {
  return {
    id: uid('split'),
    kind: 'splitter',
    version: 1,
    name,
    cols: 3,
    rows: 3,
    tileAspect: TILE_ASPECTS[0].id,
    source: makeSlot(),
    background: { type: 'solid', color: '#ffffff' },
    gutter: 6,
    order: 'instagram',
    assets: {},
    assetOrder: [],
  };
}

/* ── Seamless carousel ─────────────────────────────────────────────────── */

export function createCarousel(name = 'Untitled Carousel'): CarouselDoc {
  const preset = SLIDE_PRESETS[0];
  return {
    id: uid('carousel'),
    kind: 'carousel',
    version: 1,
    name,
    slides: 3,
    slideAspect: preset.id,
    slideW: preset.w,
    slideH: preset.h,
    background: { type: 'solid', color: '#ffffff' },
    layers: [],
    assets: {},
    assetOrder: [],
  };
}

export function makeImageLayer(assetId: ID | null, rect: { x: number; y: number; w: number; h: number }): ImageLayer {
  return {
    id: uid('layer'),
    name: 'Image',
    ...rect,
    rotation: 0,
    opacity: 100,
    locked: false,
    hidden: false,
    type: 'image',
    assetId,
    fit: 'cover',
    transform: { ...IDENTITY_TRANSFORM },
    filters: { ...NEUTRAL_FILTERS },
    radius: 0,
    shadow: { enabled: false, blur: 60, opacity: 25, y: 20, color: '#000000' },
  };
}

export function makeTextLayer(rect: { x: number; y: number; w: number; h: number }): TextLayer {
  return {
    id: uid('layer'),
    name: 'Text',
    ...rect,
    rotation: 0,
    opacity: 100,
    locked: false,
    hidden: false,
    type: 'text',
    text: 'Your headline here',
    fontId: 'system',
    size: Math.round(rect.h * 0.28),
    weight: 700,
    italic: false,
    color: '#111111',
    align: 'center',
    lineHeight: 1.2,
    letterSpacing: 0,
    autoHeight: true,
  };
}
